import { useCallback, useRef, useState } from 'react';
import {
  ITrainersData,
  ITrainersPage,
  ITrainersTool,
  ITrainerSubmit,
} from '@/types/trainers';
import {
  getTrainersContent,
  submitBenchmark,
  submitTrainersResponse,
} from '@/api/qa';
import { useTrainerFeedback } from '@/hooks/useTrainerFeedback';

import Feedback from '@/components/ui/trainers/shared/Feedback';
import { useTrainerReviews } from '@/hooks/useTrainerReviews';
import { Input } from '@/components/shadcn/input';
import { Label } from '@/components/shadcn/label';
import TrainerHeader from './TrainerHeader';
import TrainerFooter from './TrainerFooter';
import TrainerController, {
  TrainerButtons,
  TrainerInputs,
  TrainerSelect,
} from './TrainerController';
import ErrorMessage from './shared/ErrorMessage';
import SelectableImageGrid from './SelectableImageGrid';
interface Props extends ITrainersPage {
  data?: ITrainersData;
  tool?: ITrainersTool;
  modifyPosition?: boolean;
  modifyAzimuth?: boolean;
}

const GAS_CATEGORIES = [
  'Regular Cash',
  'Regular Card',
  'Midgrade Cash',
  'Midgrade Card',
  'Premium Cash',
  'Premium Card',
  'Diesel Cash',
  'Diesel Card',
];

function categoryToLowerCammerCase(category: string) {
  const str = category.replace(' ', '');
  return str.charAt(0).toLowerCase() + str.slice(1);
}

const GasPriceReading: React.FC<Props> = ({
  data: initialData,
  page,
  name,
  description: initialDescription,
  location,
  tool,
}) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const controllerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const toolId = initialData?.toolId || '';
  const [data, setData] = useState<ITrainersData | undefined>(initialData);
  const { incrementReviews } = useTrainerReviews();
  const { leaseId, phiLandmarkObectDetections = [] } = data?.content || {};

  const description =
    initialDescription ||
    (data?.content
      ? `Fill out the gas prices for different categories based on the images of the gas price sign, if you cant detect the prices leave as empty.`
      : '');

  const isDataReady = Boolean(data?.content);

  const [isBenchmark, setIsBenchmark] = useState(false);

  const { feedback, setFeedback } = useTrainerFeedback();

  const [gasPrices, setGasPrices] = useState<Record<string, number | null>>(
    Object.fromEntries(
      GAS_CATEGORIES.map(category => {
        return [categoryToLowerCammerCase(category), null];
      }),
    ),
  );

  const clearAllData = () => {
    setData(undefined);
    setIsBenchmark(false);
    setGasPrices(
      Object.fromEntries(
        GAS_CATEGORIES.map(category => {
          return [categoryToLowerCammerCase(category), null];
        }),
      ),
    );
  };

  const refetchClient = useCallback(
    async (id: string) => {
      const _data: ITrainersData = await getTrainersContent(id, page);
      setData(_data);
    },
    [page],
  );

  const onBenchmarkToggle = (checked: boolean) => {
    setIsBenchmark(checked);
  };
  const onSubmit = async ({ outcome, value }: ITrainerSubmit) => {
    clearAllData();
    if (!leaseId) {
      return;
    }
    const sending = {
      leaseId,
      outcome: outcome ?? value,
      value: JSON.stringify(gasPrices),
    };

    if (isBenchmark) {
      await submitBenchmark(toolId, sending);
    }

    const { error, benchmarkFeedback } = await submitTrainersResponse(
      toolId,
      sending,
    );

    if (benchmarkFeedback) {
      setFeedback({
        ...benchmarkFeedback,
        instructionsLink: tool?.args?.instructions,
      });
    }
    if (error) {
      setData({ error });
    } else {
      incrementReviews(toolId);
      refetchClient(toolId);
    }
  };
  return (
    <div className="flex size-full flex-col">
      <TrainerHeader
        data={data}
        name={name}
        description={description}
        location={location}
        tool={tool}
        elementRef={headerRef}
      />
      {data?.error ? (
        <ErrorMessage error={data.error} />
      ) : (
        <>
          <SelectableImageGrid
            images={phiLandmarkObectDetections.map(o => o.url)}
            bboxes={phiLandmarkObectDetections.map(o => o.bbox)}
            width={phiLandmarkObectDetections.length}
            height={1}
            selectable={false}
          />

          {tool?.queue === 'gasPriceReading' && (
            <div className="flex items-center justify-center">
              {GAS_CATEGORIES.map((category, index) => (
                <div key={category} className="inline p-2">
                  <Label>{category}</Label>
                  <Input
                    placeholder={category}
                    value={
                      gasPrices[
                        categoryToLowerCammerCase(GAS_CATEGORIES[index])
                      ] ?? ''
                    }
                    type="number"
                    onInput={(e: any) => {
                      setGasPrices(curGasPrices => {
                        const value =
                          !e.target.value || !e.target.value.length
                            ? null
                            : Number(e.target.value);
                        const newObj = Object.fromEntries(
                          Object.entries(curGasPrices),
                        );
                        newObj[
                          categoryToLowerCammerCase(GAS_CATEGORIES[index])
                        ] = value;
                        return newObj;
                      });
                    }}
                  />
                </div>
              ))}
            </div>
          )}
          <TrainerController
            containerRef={controllerRef}
            isDataReady={isDataReady}
            onSubmit={onSubmit}
            data={data}
            tool={tool}>
            <TrainerButtons />
            <TrainerSelect />
            <TrainerInputs />
          </TrainerController>
        </>
      )}
      <TrainerFooter
        data={data}
        elementRef={footerRef}
        onBenchmarkToggle={onBenchmarkToggle}
        isBenchmark={isBenchmark}
      />
      {feedback?.feedbackMessage && <Feedback />}
    </div>
  );
};

export default GasPriceReading;
